// 6-card detail header: Symbol+score / Position / Net / Realized / Unrealized / Funding
import type { GridConfig, Trade } from "@/types/api";
import type { GridPnl } from "@/hooks/use-grid-pnl";
import type { GridPosition, VolatilityStatus } from "@/hooks/use-grid-detail";
import { fmt, pnlTone } from "@/lib/format";
import StatCard from "@/components/common/stat-card";
import Badge from "@/components/common/badge";
import { TAKER_FEE_RATE } from "@/lib/grid-trade-pairing";

interface GridStatsProps {
  grid: GridConfig;
  pnl: GridPnl | undefined;
  position: GridPosition | null;
  unmatchedLegs: Trade[];
  livePrice: number | null;
  volatility: VolatilityStatus | null;
}

export default function GridStats({
  grid,
  pnl,
  position,
  unmatchedLegs,
  livePrice,
  volatility,
}: GridStatsProps) {
  // Unrealized PnL: per-leg mark-to-market
  let unrealized = 0;
  if (livePrice) {
    for (const o of unmatchedLegs) {
      const dir = o.side === "buy" ? 1 : -1;
      const closeFee = livePrice * o.quantity * TAKER_FEE_RATE;
      unrealized += (livePrice - o.price) * o.quantity * dir - (o.fee ?? 0) - closeFee;
    }
  }

  const realized = pnl?.total_pnl ?? 0;
  const funding = pnl?.funding_total ?? 0;
  const net = realized + unrealized + funding;

  // Position label
  const netSize = position?.size ?? 0;
  const netAvg = position?.avg_entry ?? 0;
  const legCount = position?.leg_count ?? 0;
  let posValue = "None";
  let posTone: "neutral" | "success" | "danger" = "neutral";
  let posSub: string | undefined;
  if (Math.abs(netSize) > 1e-9) {
    const dir = netSize > 0 ? "LONG" : "SHORT";
    posValue = `${dir} ${Math.abs(netSize).toFixed(4)}`;
    posTone = netSize > 0 ? "success" : "danger";
    posSub = `entry ${fmt(netAvg, grid.symbol)}${legCount > 0 ? ` · ${legCount} legs` : ""}`;
  } else if (legCount > 0) {
    posValue = `Net 0 · ${legCount} legs`;
  }

  // Score sub-label
  const score = pnl?.score;
  const scoreTone = !score
    ? "neutral"
    : score.total >= 75
    ? "success"
    : score.total >= 50
    ? "warn"
    : ("danger" as const);

  // Required margin = notional of max concurrent exposure / leverage.
  // Max concurrent = grid_count + 1 (N levels on one side + the carry from a fill).
  // Uses live price when available, falls back to the grid's lower bound.
  const pxRef = livePrice ?? grid.lower_price ?? 0;
  const marginReq =
    pxRef > 0 && grid.leverage > 0
      ? (grid.quantity_per_grid * pxRef * (grid.grid_count + 1)) / grid.leverage
      : null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
      {/* Symbol + score */}
      <div className="bg-bg border border-border rounded-lg p-3">
        <div className="text-muted text-[0.7rem] uppercase tracking-widest">Symbol</div>
        <div className="text-sm font-semibold mt-0.5 text-text flex flex-wrap items-center gap-1">
          {grid.symbol}
          {livePrice && (
            <span className="text-warn text-xs font-normal">{fmt(livePrice, grid.symbol)}</span>
          )}
          {volatility?.mode === "fast" && (
            <Badge color="danger" title={`Fast market — velocity ${(volatility.velocity * 100).toFixed(2)}%/60s`}>
              FAST
            </Badge>
          )}
        </div>
        <div
          className="text-xs mt-0.5"
          title={
            score
              ? `Grid score: ROI ${score.breakdown.roi}/40 · Bps ${score.breakdown.bps}/30 · Fee ${score.breakdown.fee}/15 · WinRate ${score.breakdown.win_rate}/15`
              : undefined
          }
        >
          {marginReq != null && (
            <span
              className="text-text font-medium"
              title={`Required margin = qty/grid (${grid.quantity_per_grid}) × price × (grid_count+1=${grid.grid_count + 1}) / leverage (${grid.leverage}x)`}
            >
              {fmt(marginReq, 2)} USDT
            </span>
          )}
          {marginReq != null && score && <span className="text-muted mx-1">·</span>}
          {score && (
            <>
              <span className={`font-semibold text-${scoreTone}`}>{score.total}</span>
              <span className="text-muted">/100</span>
              <span className="text-muted ml-1 text-[0.65rem]">
                {score.win_rate_pct.toFixed(1)}% win
              </span>
            </>
          )}
        </div>
      </div>

      {/* Position */}
      <StatCard
        label="Position"
        value={posValue}
        sub={posSub}
        tone={posTone}
      />

      {/* Net */}
      <StatCard
        label="Net"
        value={pnl ? (net >= 0 ? "+" : "") + fmt(net, 4) : "—"}
        tone={pnlTone(net)}
        title="Net PnL = Realized + Unrealized + Funding"
      />

      {/* Realized */}
      <StatCard
        label="Realized PnL"
        value={pnl ? fmt(realized, 4) : "—"}
        sub="net of fees"
        tone={pnlTone(realized)}
        title="Realized PnL from closed pairs, net of trading fees on both legs"
      />

      {/* Unrealized */}
      <StatCard
        label="Unrealized PnL"
        value={fmt(unrealized, 4)}
        sub="incl. fees"
        tone={pnlTone(unrealized)}
        title="Mark-to-market on open positions, including opening fee paid and estimated taker close fee"
      />

      {/* Funding */}
      <StatCard
        label="Funding"
        value={pnl ? fmt(funding, 4) : "—"}
        tone={pnlTone(funding)}
        title="Perpetual funding fees (settled every 8h). Net = Realized + Unrealized + Funding."
      />
    </div>
  );
}
